import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  LOCATION_DESCRIPTIONS,
  SETUP_FREQUENCY_LABEL,
  getEquipmentHelpText,
  getSetupFrequencyOptions,
} from '@personal-trainer-app/shared/src/utils/questionnaire/questionnaire-form';
import {
  EQUIPMENT_ACCESS_OPTIONS,
  TIME_COMMITMENT_OPTIONS,
  WORKOUT_LOCATION_OPTIONS,
} from '@personal-trainer-app/shared/src/utils/select-options';

import { ChipMultiSelect } from '../ChipMultiSelect';

import type { SetupStepProps } from './SetupStep.types';
import type { ChoiceCardOption } from '@/components/ui/ChoiceCardSelect';
import type { FitnessQuestionnaireInput as QuestionnaireFormValues } from '@personal-trainer-app/shared';

import { FormField } from '@/components/forms';
import { ChoiceCardSelect } from '@/components/ui/ChoiceCardSelect';
import { RadioGroup } from '@/components/ui/Radio';

const LOCATION_CARDS: ChoiceCardOption[] = WORKOUT_LOCATION_OPTIONS.map(
  (option) => ({
    value: option.value,
    title: option.label,
    description: LOCATION_DESCRIPTIONS[option.value] ?? '',
  })
);

/**
 * Step 3 — Your setup: the four answers the plan generator sizes a plan by.
 *
 * - `primary_workout_location` — choice cards
 * - `equipment_on_hand` — chips, plus a typed "Other"
 * - `current_days_of_exercise` — sets workouts per week
 * - `minutes_of_workout` — sizes each session
 *
 * Required here (the sign-up schema; equipment is optional at the gym);
 * optional in Refine your plan, which draws the same fields with this
 * component and `context="refine"`.
 */
export function SetupStep({
  context = 'sign-up',
}: SetupStepProps): React.JSX.Element {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<QuestionnaireFormValues>();
  const location = watch('primary_workout_location');

  return (
    <div className="space-y-6">
      <Controller
        name="primary_workout_location"
        control={control}
        render={({ field }) => (
          <FormField
            label="Where do you usually train?"
            error={errors.primary_workout_location?.message}
          >
            <ChoiceCardSelect
              options={LOCATION_CARDS}
              value={field.value}
              onChange={field.onChange}
              ariaLabel="Primary workout location"
            />
          </FormField>
        )}
      />
      <Controller
        name="equipment_on_hand"
        control={control}
        render={({ field }) => (
          <FormField
            label="What equipment can you use?"
            helpText={getEquipmentHelpText(context, location)}
            error={errors.equipment_on_hand?.message}
          >
            <ChipMultiSelect
              options={EQUIPMENT_ACCESS_OPTIONS}
              value={field.value ?? []}
              onChange={field.onChange}
              allowOther
              ariaLabel="Equipment on hand"
            />
          </FormField>
        )}
      />
      <Controller
        name="current_days_of_exercise"
        control={control}
        render={({ field }) => (
          <RadioGroup
            name="current_days_of_exercise"
            label={SETUP_FREQUENCY_LABEL[context]}
            variant="pill"
            options={[...getSetupFrequencyOptions(context)]}
            value={field.value ?? ''}
            onChange={field.onChange}
            error={errors.current_days_of_exercise?.message}
          />
        )}
      />
      <Controller
        name="minutes_of_workout"
        control={control}
        render={({ field }) => (
          <RadioGroup
            name="minutes_of_workout"
            label="How long is a typical session?"
            variant="pill"
            options={[...TIME_COMMITMENT_OPTIONS]}
            value={field.value ?? ''}
            onChange={field.onChange}
            error={errors.minutes_of_workout?.message}
          />
        )}
      />
    </div>
  );
}

export default SetupStep;
